import { powersyncLogger } from '../logger';

/**
 * Columns held locally as JSON text and sent to the API as the documents they are.
 *
 * SQLite has no json type, so PowerSync's schema can only declare these `text`: the value is
 * stringified on the way in and comes back out of a CRUD op as a string. The Laravel endpoints
 * validate the document's own fields — `boat_data.model`, `boat_data.unit`,
 * `boat_data.manufacturer.name` — and a string has none of them, so every one of those rules fails
 * at once and the write is rejected. Parsing here is what makes the payload a document again.
 */
const JSON_COLUMNS: Record<string, readonly string[]> = {
  boat_models: ['boat_data'],
};

/**
 * The row as the API takes it, with its JSON columns parsed.
 *
 * A column that cannot be parsed is left as it is rather than thrown over: a throw here has no HTTP
 * status, which this connector treats as retryable, so an unparseable row would block every write
 * queued behind it forever. Sent as a string it is rejected once, recorded as a validation error
 * against that row, and the queue moves on — and the log line below says which row to look at.
 */
export const parseJsonColumns = (tableName: string, record: Record<string, unknown>): Record<string, unknown> => {
  const columns = JSON_COLUMNS[tableName];

  if (!columns) {
    return record;
  }

  const parsed = { ...record };

  for (const column of columns) {
    const value = parsed[column];

    if (typeof value !== 'string') {
      continue;
    }

    try {
      parsed[column] = JSON.parse(value);
    } catch (ex) {
      powersyncLogger.exception('json column could not be parsed; sending it as text', ex, {
        table: tableName,
        column,
      });
    }
  }

  return parsed;
};
